// PodcastAnalyticsDashboard.js
import React, { useState, useEffect, useMemo } from "react";
import { Graph } from "./SectionScreens/GraphDisplay";
import { GoogleMap, LoadScript } from "@react-google-maps/api";
import { firestore, storage } from "../helpers/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import {
  logAnalyticsDashboardFilter,
  logAnalyticsDashboardMapLayerToggle,
} from "../helpers/AnalyticsTags";
import AnalyticsHeatmapLayer from "./PodcastAnalytics/AnalyticsHeatmapLayer";
import {
  aggregateHeatmapPoints,
  buildSelectedHeatmapSourcePoints,
} from "./PodcastAnalytics/heatmapUtils";
import Papa from "papaparse";
import "./PodcastAnalyticsDashboard.css";

const GOOGLE_MAPS_API_KEY = "AIzaSyASU8wb6swDIo3dNxkyjfraWijCImc3-OU";
const DEFAULT_TITLE = "Usage Analytics";
const UNKNOWN_PLATFORM = "unknown platform";
const UNKNOWN_CITY = "unknown city";

const CENTER = { lat: 37.721941, lng: -122.127001 };


const DARK_MAP_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }],
  },
  { featureType: "poi", elementType: "all", stylers: [{ visibility: "off" }] },
  {
    featureType: "landscape.natural",
    elementType: "geometry",
    stylers: [{ color: "#223322" }],
  },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#38414e" }] },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#212a37" }],
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: "#9ca5b3" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#746855" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#1f2835" }],
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [{ color: "#f3d19c" }],
  },
  { featureType: "transit", elementType: "geometry", stylers: [{ color: "#2f3948" }] },
  {
    featureType: "transit.station",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }],
  },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#17263c" }] },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#515c6d" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.stroke",
    stylers: [{ color: "#17263c" }],
  },
];

// colors used in charts & map
const SERIES_COLORS = {
  engaged: "#42a5f5",
  played: "#4caf50",
  nextSteps: "#ff9800",
};

const NEXT_STEP_COLORS = {
  alerts: "#4caf50",
  zones: "#03a9f4",
  zone: "#03a9f4",
  nixle: "#ff9800",
};

const PLATFORM_COLORS = {
  mobile: "#42a5f5",
  desktop: "#66bb6a",
  tablet: "#ab47bc",
};

const getRawRowDate = (row) =>
  String(row["date_"] ?? row["Date"] ?? row["date"] ?? "").trim();

const getRawRowPlatform = (row) =>
  (
    String(
    row["device_category"] ??
      row["deviceCategory"] ??
      row["DeviceCategory"] ??
      row["platform"] ??
      row["Platform"] ??
      ""
    )
      .trim()
      .toLowerCase() || UNKNOWN_PLATFORM
  );

const getRawRowCity = (row) =>
  (
    String(row["city"] ?? row["City"] ?? "")
      .trim()
      .toLowerCase() || UNKNOWN_CITY
  );

const getValidLocation = (location) => {
  const lat = Number(location?.lat);
  const lng = Number(location?.lng);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return { lat, lng };
};

const PodcastAnalyticsDashboard = ({ storagePath, title = DEFAULT_TITLE, podcastId }) => {
  const [rawRows, setRawRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapCenter, setMapCenter] = useState(CENTER);
  const [analyticsMap, setAnalyticsMap] = useState(null);

  const [selectedDateLabels, setSelectedDateLabels] = useState([]);
  const [hasInitializedDateSelection, setHasInitializedDateSelection] =
    useState(false);
  const [selectedPlatformValues, setSelectedPlatformValues] = useState([]);
  const [hasInitializedPlatformSelection, setHasInitializedPlatformSelection] =
    useState(false);
  const [selectedCityValues, setSelectedCityValues] = useState([]);
  const [hasInitializedCitySelection, setHasInitializedCitySelection] =
    useState(false);

  // map filters
  const [showPlayed, setShowPlayed] = useState(false);
  const [showPlayed90Sec, setShowPlayed90Sec] = useState(false);
  const [showReachedEnd, setShowReachedEnd] = useState(false);
  const [showNextSteps, setShowNextSteps] = useState(false);
  const [funnelBaseline, setFunnelBaseline] = useState("visits");

  const logFilterChange = ({
    filterType,
    action,
    value = "",
    selectedCount,
    totalCount,
  }) => {
    logAnalyticsDashboardFilter({
      podcastId,
      filterType,
      action,
      value,
      selectedCount,
      totalCount,
    });
  };

  const handleSelectAllDates = () => {
    setSelectedDateLabels(availableDateLabels);
    logFilterChange({
      filterType: "date",
      action: "select_all",
      selectedCount: availableDateLabels.length,
      totalCount: availableDateLabels.length,
    });
  };

  const handleClearDates = () => {
    setSelectedDateLabels([]);
    logFilterChange({
      filterType: "date",
      action: "clear",
      selectedCount: 0,
      totalCount: availableDateLabels.length,
    });
  };

  const handleToggleDate = (label) => {
    const isSelected = selectedDateLabels.includes(label);
    const nextSelectedCount = isSelected
      ? selectedDateLabels.length - 1
      : selectedDateLabels.length + 1;

    setSelectedDateLabels((prev) =>
      prev.includes(label)
        ? prev.filter((value) => value !== label)
        : [...prev, label]
    );
    logFilterChange({
      filterType: "date",
      action: isSelected ? "remove" : "add",
      value: label,
      selectedCount: nextSelectedCount,
      totalCount: availableDateLabels.length,
    });
  };

  const handleSelectAllPlatforms = () => {
    setSelectedPlatformValues(availablePlatformValues);
    logFilterChange({
      filterType: "platform",
      action: "select_all",
      selectedCount: availablePlatformValues.length,
      totalCount: availablePlatformValues.length,
    });
  };

  const handleClearPlatforms = () => {
    setSelectedPlatformValues([]);
    logFilterChange({
      filterType: "platform",
      action: "clear",
      selectedCount: 0,
      totalCount: availablePlatformValues.length,
    });
  };

  const handleTogglePlatform = (value) => {
    const isSelected = selectedPlatformValues.includes(value);
    const nextSelectedCount = isSelected
      ? selectedPlatformValues.length - 1
      : selectedPlatformValues.length + 1;

    setSelectedPlatformValues((prev) =>
      prev.includes(value)
        ? prev.filter((platform) => platform !== value)
        : [...prev, value]
    );
    logFilterChange({
      filterType: "platform",
      action: isSelected ? "remove" : "add",
      value,
      selectedCount: nextSelectedCount,
      totalCount: availablePlatformValues.length,
    });
  };

  const handleSelectAllCities = () => {
    setSelectedCityValues(availableCityValues);
    logFilterChange({
      filterType: "city",
      action: "select_all",
      selectedCount: availableCityValues.length,
      totalCount: availableCityValues.length,
    });
  };

  const handleClearCities = () => {
    setSelectedCityValues([]);
    logFilterChange({
      filterType: "city",
      action: "clear",
      selectedCount: 0,
      totalCount: availableCityValues.length,
    });
  };

  const handleToggleCity = (value) => {
    const isSelected = selectedCityValues.includes(value);
    const nextSelectedCount = isSelected
      ? selectedCityValues.length - 1
      : selectedCityValues.length + 1;

    setSelectedCityValues((prev) =>
      prev.includes(value)
        ? prev.filter((city) => city !== value)
        : [...prev, value]
    );
    logFilterChange({
      filterType: "city",
      action: isSelected ? "remove" : "add",
      value,
      selectedCount: nextSelectedCount,
      totalCount: availableCityValues.length,
    });
  };

  const handleMapLayerToggle = (layer, checked, setChecked) => {
    setChecked(checked);
    logAnalyticsDashboardMapLayerToggle({
      podcastId,
      layer,
      checked,
    });
  };

  useEffect(() => {
    let isMounted = true;

    const fetchPodcastMapCenter = async () => {
      if (!podcastId) {
        setMapCenter(CENTER);
        return;
      }

      try {
        const podcastSnap = await getDoc(doc(firestore, "podcasts", podcastId));
        const defaultLocation = getValidLocation(
          podcastSnap.exists() ? podcastSnap.data()?.default_location : null
        );

        if (isMounted) {
          setMapCenter(defaultLocation || CENTER);
        }
      } catch (err) {
        console.error("Error loading podcast default location:", err);
        if (isMounted) {
          setMapCenter(CENTER);
        }
      }
    };

    fetchPodcastMapCenter();

    return () => {
      isMounted = false;
    };
  }, [podcastId]);

  // ───────────── Load CSV from Firebase Storage ─────────────
  useEffect(() => {
    let isMounted = true;

    const fetchCsv = async () => {
      if (!storagePath) {
        setRawRows([]);
        setError("Missing analytics CSV path");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setRawRows([]);

      try {
        const fileRef = ref(storage, storagePath);
        const url = await getDownloadURL(fileRef);
        const res = await fetch(url);
        const text = await res.text();

        const parsed = Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
        });

        if (isMounted) {
          setRawRows(parsed.data || []);
        }
      } catch (err) {
        console.error("Error loading CSV:", err);
        if (isMounted) {
          setError(err.message || "Failed to load data");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCsv();

    return () => {
      isMounted = false;
    };
  }, [storagePath]);

  const availableDateLabels = useMemo(() => {
    const seen = new Set();
    const labels = [];

    rawRows.forEach((row) => {
      const label = getRawRowDate(row);
      if (!label || seen.has(label)) return;
      seen.add(label);
      labels.push(label);
    });

    return labels;
  }, [rawRows]);

  const availablePlatformValues = useMemo(() => {
    const seen = new Set();
    const values = [];

    rawRows.forEach((row) => {
      const value = getRawRowPlatform(row);
      if (seen.has(value)) return;
      seen.add(value);
      values.push(value);
    });

    return values;
  }, [rawRows]);

  const availableCityValues = useMemo(() => {
    const seen = new Set();
    const values = [];

    rawRows.forEach((row) => {
      const value = getRawRowCity(row);
      if (seen.has(value)) return;
      seen.add(value);
      values.push(value);
    });

    return values.sort((a, b) => a.localeCompare(b));
  }, [rawRows]);

  useEffect(() => {
    if (!availableDateLabels.length) {
      setSelectedDateLabels([]);
      setHasInitializedDateSelection(false);
      return;
    }

    if (!hasInitializedDateSelection) {
      setSelectedDateLabels(availableDateLabels);
      setHasInitializedDateSelection(true);
      return;
    }

    const availableSet = new Set(availableDateLabels);
    setSelectedDateLabels((prev) => {
      const next = prev.filter((value) => availableSet.has(value));
      return next.length === prev.length ? prev : next;
    });
  }, [
    availableDateLabels,
    hasInitializedDateSelection,
  ]);

  useEffect(() => {
    if (!availablePlatformValues.length) {
      setSelectedPlatformValues([]);
      setHasInitializedPlatformSelection(false);
      return;
    }

    if (!hasInitializedPlatformSelection) {
      setSelectedPlatformValues(availablePlatformValues);
      setHasInitializedPlatformSelection(true);
      return;
    }

    const availableSet = new Set(availablePlatformValues);
    setSelectedPlatformValues((prev) => {
      const next = prev.filter((value) => availableSet.has(value));
      return next.length === prev.length ? prev : next;
    });
  }, [
    availablePlatformValues,
    hasInitializedPlatformSelection,
  ]);

  useEffect(() => {
    if (!availableCityValues.length) {
      setSelectedCityValues([]);
      setHasInitializedCitySelection(false);
      return;
    }

    if (!hasInitializedCitySelection) {
      setSelectedCityValues(availableCityValues);
      setHasInitializedCitySelection(true);
      return;
    }

    const availableSet = new Set(availableCityValues);
    setSelectedCityValues((prev) => {
      const next = prev.filter((value) => availableSet.has(value));
      return next.length === prev.length ? prev : next;
    });
  }, [
    availableCityValues,
    hasInitializedCitySelection,
  ]);

  const filteredRows = useMemo(() => {
    const hasDateFilterOptions = availableDateLabels.length > 0;
    const hasPlatformFilterOptions = availablePlatformValues.length > 0;
    const hasCityFilterOptions = availableCityValues.length > 0;

    if (hasDateFilterOptions && !selectedDateLabels.length) return [];
    if (hasPlatformFilterOptions && !selectedPlatformValues.length) return [];
    if (hasCityFilterOptions && !selectedCityValues.length) return [];

    const selectedDates = new Set(selectedDateLabels);
    const selectedPlatforms = new Set(selectedPlatformValues);
    const selectedCities = new Set(selectedCityValues);

    return rawRows.filter((row) => {
      if (hasDateFilterOptions) {
        const dateLabel = getRawRowDate(row);
        if (!dateLabel || !selectedDates.has(dateLabel)) return false;
      }

      if (hasPlatformFilterOptions) {
        const platformValue = getRawRowPlatform(row);
        if (!selectedPlatforms.has(platformValue)) return false;
      }

      if (hasCityFilterOptions) {
        const cityValue = getRawRowCity(row);
        if (!selectedCities.has(cityValue)) return false;
      }

      return true;
    });
  }, [
    rawRows,
    availableDateLabels,
    availablePlatformValues,
    availableCityValues,
    selectedDateLabels,
    selectedPlatformValues,
    selectedCityValues,
  ]);

  // ───────────── Compute graph + map data from rows ─────────────
  const {
    multiLineGraphData,
    nextStepsBarGraphData,
    platformDonutGraphData,
    playedPoints,
    played90SecPoints,
    reachedEndPoints,
    nextStepsPoints,
    engagedTotal,
    playedTotal,
    played90SecTotal,
    reachedEndTotal,
    nextStepsUserTotal,
    nextStepsActionTotal,
  } = useMemo(() => {
    const byDate = new Map();
    const nextStepCounts = {};
    const platformCounts = {};
    const playedPts = [];
    const played90SecPts = [];
    const reachedEndPts = [];
    const nextPts = [];
    let totalEngaged = 0;
    let totalPlayed = 0;
    let totalPlayed90Sec = 0;
    let totalReachedEnd = 0;
    let totalNextStepUsers = 0;
    let totalNextStepActions = 0;

    const parseBool = (v) =>
      String(v ?? "")
        .toLowerCase()
        .trim() === "true";

    filteredRows.forEach((row) => {
      const dateLabel = getRawRowDate(row);
      const platformValue = getRawRowPlatform(row);

      totalEngaged += 1;
      const didPlay = parseBool(row["didPlay"] ?? row["DidPlay"]);
      const didReachEnd = parseBool(row["didReachEnd"] ?? row["DidReachEnd"]);
      const didPlayAfter90Sec = parseBool(
        row["didPlayAfter90Seconds"] ??
          row["didPlayAfter90Sec"] ??
          row["DidPlayAfter90Seconds"] ??
          row["DidPlayAfter90Sec"]
      );
      const didClickNextStep = parseBool(
        row["didClickNextStep"] ?? row["DidClickNextStep"]
      );
      const nextStepsClicked =
        row["nextStepsClicked"] ?? row["NextStepsClicked"] ?? "";
      const parsedNextSteps = nextStepsClicked
        ? nextStepsClicked
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean)
        : [];
      const nextStepActionsCount = parsedNextSteps.length;
      const tookNextStep = didClickNextStep || nextStepActionsCount > 0;

      // ------- per-day aggregates for multiline chart -------
      if (dateLabel) {
        if (!byDate.has(dateLabel)) {
          byDate.set(dateLabel, {
            date: dateLabel,
            engaged: 0,
            played: 0,
            nextSteps: 0,
          });
        }
        const stats = byDate.get(dateLabel);
        stats.engaged += 1; // every row is an engagement
        if (didPlay) {
          stats.played += 1;
          totalPlayed += 1;
        }
        if (didPlayAfter90Sec) {
          totalPlayed90Sec += 1;
        }
        if (didReachEnd) {
          totalReachedEnd += 1;
        }
        if (tookNextStep) {
          stats.nextSteps += 1;
          totalNextStepUsers += 1;
        }
      } else {
        if (didPlay) totalPlayed += 1;
        if (didPlayAfter90Sec) totalPlayed90Sec += 1;
        if (didReachEnd) totalReachedEnd += 1;
        if (tookNextStep) totalNextStepUsers += 1;
      }

      // ------- breakdown of next-step types for bar chart -------
      if (nextStepActionsCount > 0) {
        parsedNextSteps.forEach((labelRaw) => {
          const lower = labelRaw.toLowerCase();
          nextStepCounts[lower] = (nextStepCounts[lower] || 0) + 1;
          totalNextStepActions += 1;
        });
      } else if (didClickNextStep) {
        totalNextStepActions += 1;
      }

      // ------- platform distribution for donut chart -------
      if (platformValue) {
        platformCounts[platformValue] = (platformCounts[platformValue] || 0) + 1;
      }

      // ------- map points (played / nextSteps) -------
      const locationStr = row["Location"] || row["location"];
      if (locationStr && locationStr.trim() !== "") {
        const [latStr, lngStr] = locationStr.split(",");
        const lat = parseFloat(latStr);
        const lng = parseFloat(lngStr);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          if (didPlay) playedPts.push({ lat, lng });
          if (didPlayAfter90Sec) played90SecPts.push({ lat, lng });
          if (didReachEnd) reachedEndPts.push({ lat, lng });
          if (tookNextStep) {
            nextPts.push({ lat, lng });
          }
        }
      }
    });

    // keep dates in insertion order
    const dates = Array.from(byDate.values());

    const engagedSeries = {
      name: "Visits",
      data: dates.map((d) => ({
        key: d.date,
        value: d.engaged,
        color: SERIES_COLORS.engaged,
      })),
    };

    const playedSeries = {
      name: "Played podcast",
      data: dates.map((d) => ({
        key: d.date,
        value: d.played,
        color: SERIES_COLORS.played,
      })),
    };

    const nextStepsSeries = {
      name: "Clicked next step",
      data: dates.map((d) => ({
        key: d.date,
        value: d.nextSteps,
        color: SERIES_COLORS.nextSteps,
      })),
    };

    const multiLineGraphData = [engagedSeries, playedSeries, nextStepsSeries];

    const nextStepsBarGraphData = Object.entries(nextStepCounts).map(
      ([labelLower, value]) => ({
        key: labelLower, // e.g., "alerts", "zones", "nixle"
        value,
        color: NEXT_STEP_COLORS[labelLower] || "#9e9e9e",
      })
    );

    const platformDonutGraphData = Object.entries(platformCounts).map(
      ([platform, value]) => ({
        key: platform,
        value,
        color: PLATFORM_COLORS[platform] || "#90a4ae",
      })
    );

    return {
      multiLineGraphData,
      nextStepsBarGraphData,
      platformDonutGraphData,
      playedPoints: playedPts,
      played90SecPoints: played90SecPts,
      reachedEndPoints: reachedEndPts,
      nextStepsPoints: nextPts,
      engagedTotal: totalEngaged,
      playedTotal: totalPlayed,
      played90SecTotal: totalPlayed90Sec,
      reachedEndTotal: totalReachedEnd,
      nextStepsUserTotal: totalNextStepUsers,
      nextStepsActionTotal: totalNextStepActions,
    };
  }, [filteredRows]);

  // GraphDisplay expects an array of graph configs
  const graphs = useMemo(
    () => [
      {
        title: "Engagement",
        type: "multiline",
        keyAxisLabel: "Date",
        valueAxisLabel: "Sessions",
        displayText: "",
        graphData: multiLineGraphData,
      },
      {
        title: "Platform breakdown",
        type: "donut",
        displayText: "",
        centerLabel: `${engagedTotal.toLocaleString()} total`,
        graphData: platformDonutGraphData,
      },
    ],
    [multiLineGraphData, engagedTotal, platformDonutGraphData]
  );

  const summaryGraphs = useMemo(() => {
    const baselineTotal = funnelBaseline === "played" ? playedTotal : engagedTotal;
    const percentageOfBaseline = (value) => (
      baselineTotal ? `${Math.round((value / baselineTotal) * 100)}%` : "0%"
    );
    const funnelData = [
      ...(funnelBaseline === "visits"
        ? [{ key: "Visits", value: engagedTotal, color: SERIES_COLORS.engaged, percent: null }]
        : []),
      {
        key: "Played experience",
        value: playedTotal,
        color: SERIES_COLORS.played,
        percent: funnelBaseline === "played" ? null : percentageOfBaseline(playedTotal),
      },
      {
        key: "Played 90+ sec",
        value: played90SecTotal,
        color: "#7e57c2",
        percent: percentageOfBaseline(played90SecTotal),
      },
      {
        key: "Reached end",
        value: reachedEndTotal,
        color: "#26c6da",
        percent: percentageOfBaseline(reachedEndTotal),
      },
      {
        key: "Next-step clicks",
        value: nextStepsUserTotal,
        color: SERIES_COLORS.nextSteps,
        percent: percentageOfBaseline(nextStepsUserTotal),
      },
    ];

    return [
      {
        title: "Engagement Funnel",
        type: "funnel",
        displayText: "",
        graphData: funnelData,
      },
      {
        title: "Next Steps Overview",
        type: "donut",
        displayText: "",
        centerLabel: `${nextStepsActionTotal.toLocaleString()} total`,
        graphData: nextStepsBarGraphData,
      },
    ];
  }, [
      funnelBaseline,
      engagedTotal,
      playedTotal,
      played90SecTotal,
      reachedEndTotal,
      nextStepsUserTotal,
      nextStepsActionTotal,
      nextStepsBarGraphData,
    ]);

  // combine selected analytics layers into one privacy-aggregated heatmap
  const { heatmapPoints, heatmapMaxIntensity } = useMemo(() => {
    const aggregated = aggregateHeatmapPoints(
      buildSelectedHeatmapSourcePoints({
        playedPoints,
        played90SecPoints,
        reachedEndPoints,
        nextStepsPoints,
        showPlayed,
        showPlayed90Sec,
        showReachedEnd,
        showNextSteps,
      })
    );

    return {
      heatmapPoints: aggregated.points,
      heatmapMaxIntensity: aggregated.maxIntensity,
    };
  }, [
    playedPoints,
    played90SecPoints,
    reachedEndPoints,
    nextStepsPoints,
    showPlayed,
    showPlayed90Sec,
    showReachedEnd,
    showNextSteps,
  ]);

  // ───────────── Render ─────────────
  if (loading) {
    return (
      <div
        style={{
          backgroundColor: "#121212",
          color: "#fff",
          minHeight: "100vh",
          padding: 24,
          fontFamily: "sans-serif",
        }}
      >
        <h1>{title}</h1>
        <p>Loading data…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          backgroundColor: "#121212",
          color: "#fff",
          minHeight: "100vh",
          padding: 24,
          fontFamily: "sans-serif",
        }}
      >
        <h1>{title}</h1>
        <p style={{ color: "tomato" }}>Error: {error}</p>
      </div>
    );
  }

  const filterDetailsStyle = {
    background: "#1f1f1f",
    border: "1px solid #444",
    borderRadius: 8,
    padding: "8px 10px",
    width: "fit-content",
    position: "relative",
    zIndex: 20,
  };

  const filterPanelStyle = {
    position: "absolute",
    top: "calc(100% + 8px)",
    left: 0,
    zIndex: 30,
    background: "#1f1f1f",
    border: "1px solid #444",
    borderRadius: 8,
    padding: 12,
    minWidth: "100%",
    boxShadow: "0 12px 28px rgba(0, 0, 0, 0.45)",
  };

  return (
    <div className="podcast-analytics-page">
      <h1>{title}</h1>

      <div className="podcast-analytics-filters">
        <details
          className="podcast-analytics-filter podcast-analytics-filter-wide"
          style={{
            ...filterDetailsStyle,
            zIndex: 23,
          }}
        >
          <summary style={{ cursor: "pointer", userSelect: "none" }}>
            Dates ({selectedDateLabels.length}/{availableDateLabels.length})
          </summary>

          <div className="podcast-analytics-filter-panel" style={filterPanelStyle}>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={handleSelectAllDates}
                style={{
                  background: "#2e7d32",
                  color: "#fff",
                  border: "none",
                  borderRadius: 4,
                  padding: "4px 10px",
                  cursor: "pointer",
                }}
              >
                Select all
              </button>
              <button
                type="button"
                onClick={handleClearDates}
                style={{
                  background: "#b71c1c",
                  color: "#fff",
                  border: "none",
                  borderRadius: 4,
                  padding: "4px 10px",
                  cursor: "pointer",
                }}
              >
                Clear
              </button>
            </div>

            <div
              style={{
                marginTop: 10,
                maxHeight: 220,
                overflowY: "auto",
                paddingRight: 8,
              }}
            >
              {availableDateLabels.map((label) => {
                const checked = selectedDateLabels.includes(label);
                return (
                  <label
                    key={label}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 6,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleToggleDate(label)}
                    />
                    {label}
                  </label>
                );
              })}
            </div>
          </div>
        </details>

        <details
          className="podcast-analytics-filter"
          style={{
            ...filterDetailsStyle,
            zIndex: 22,
          }}
        >
          <summary style={{ cursor: "pointer", userSelect: "none" }}>
            Platform ({selectedPlatformValues.length}/{availablePlatformValues.length})
          </summary>

          <div className="podcast-analytics-filter-panel" style={filterPanelStyle}>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={handleSelectAllPlatforms}
                style={{
                  background: "#2e7d32",
                  color: "#fff",
                  border: "none",
                  borderRadius: 4,
                  padding: "4px 10px",
                  cursor: "pointer",
                }}
              >
                Select all
              </button>
              <button
                type="button"
                onClick={handleClearPlatforms}
                style={{
                  background: "#b71c1c",
                  color: "#fff",
                  border: "none",
                  borderRadius: 4,
                  padding: "4px 10px",
                  cursor: "pointer",
                }}
              >
                Clear
              </button>
            </div>

            <div
              style={{
                marginTop: 10,
                maxHeight: 220,
                overflowY: "auto",
                paddingRight: 8,
              }}
            >
              {availablePlatformValues.map((value) => {
                const checked = selectedPlatformValues.includes(value);
                return (
                  <label
                    key={value}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 6,
                      whiteSpace: "nowrap",
                      textTransform: "capitalize",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleTogglePlatform(value)}
                    />
                    {value}
                  </label>
                );
              })}
            </div>
          </div>
        </details>

        <details
          className="podcast-analytics-filter"
          style={{
            ...filterDetailsStyle,
            zIndex: 21,
          }}
        >
          <summary style={{ cursor: "pointer", userSelect: "none" }}>
            City ({selectedCityValues.length}/{availableCityValues.length})
          </summary>

          <div className="podcast-analytics-filter-panel" style={filterPanelStyle}>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={handleSelectAllCities}
                style={{
                  background: "#2e7d32",
                  color: "#fff",
                  border: "none",
                  borderRadius: 4,
                  padding: "4px 10px",
                  cursor: "pointer",
                }}
              >
                Select all
              </button>
              <button
                type="button"
                onClick={handleClearCities}
                style={{
                  background: "#b71c1c",
                  color: "#fff",
                  border: "none",
                  borderRadius: 4,
                  padding: "4px 10px",
                  cursor: "pointer",
                }}
              >
                Clear
              </button>
            </div>

            <div
              style={{
                marginTop: 10,
                maxHeight: 220,
                overflowY: "auto",
                paddingRight: 8,
              }}
            >
              {availableCityValues.map((value) => {
                const checked = selectedCityValues.includes(value);
                return (
                  <label
                    key={value}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 6,
                      whiteSpace: "nowrap",
                      textTransform: "capitalize",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleToggleCity(value)}
                    />
                    {value}
                  </label>
                );
              })}
            </div>
          </div>
        </details>
      </div>

      <div className="podcast-analytics-grid">
        <div className="podcast-analytics-chart">
          <Graph data={graphs[0]} index={0} />
        </div>
        <div className="podcast-analytics-chart">
          <Graph
            data={summaryGraphs[0]}
            index={1}
            headerContent={(
              <label className="podcast-analytics-funnelBaseline">
                <span>Baseline</span>
                <select
                  value={funnelBaseline}
                  onChange={(event) => {
                    const value = event.target.value;
                    setFunnelBaseline(value);
                    logFilterChange({
                      filterType: "funnel_baseline",
                      action: "select",
                      value,
                      selectedCount: 1,
                      totalCount: 2,
                    });
                  }}
                >
                  <option value="visits">Visit level</option>
                  <option value="played">Played level</option>
                </select>
              </label>
            )}
          />
        </div>
      </div>

      <div className="podcast-analytics-grid">
        <div className="podcast-analytics-chart">
          <Graph data={graphs[1]} index={2} />
        </div>
        <div className="podcast-analytics-chart">
          <Graph data={summaryGraphs[1]} index={3} />
        </div>
      </div>

      {/* Map */}
      <h2 style={{ marginTop: 40 }}>Start-Location Heatmap</h2>
      <div
        style={{
          margin: "12px 0",
          display: "flex",
          flexWrap: "wrap",
          gap: 16,
          alignItems: "center",
        }}
      >
        <label style={{ whiteSpace: "nowrap" }}>
          <input
            type="checkbox"
            checked={showPlayed}
            onChange={(event) =>
              handleMapLayerToggle("played", event.target.checked, setShowPlayed)
            }
          />{" "}
          Played
        </label>
        <label style={{ whiteSpace: "nowrap" }}>
          <input
            type="checkbox"
            checked={showPlayed90Sec}
            onChange={(event) =>
              handleMapLayerToggle(
                "played_90_sec",
                event.target.checked,
                setShowPlayed90Sec
              )
            }
          />{" "}
          Played 90+ sec
        </label>
        <label style={{ whiteSpace: "nowrap" }}>
          <input
            type="checkbox"
            checked={showReachedEnd}
            onChange={(event) =>
              handleMapLayerToggle(
                "reached_end",
                event.target.checked,
                setShowReachedEnd
              )
            }
          />{" "}
          Reached end
        </label>
        <label style={{ whiteSpace: "nowrap" }}>
          <input
            type="checkbox"
            checked={showNextSteps}
            onChange={(event) =>
              handleMapLayerToggle(
                "next_steps",
                event.target.checked,
                setShowNextSteps
              )
            }
          />{" "}
          Next steps
        </label>
      </div>

      <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
        <GoogleMap
          mapContainerClassName="podcast-analytics-map"
          center={mapCenter}
          zoom={14.5}
          options={{ styles: DARK_MAP_STYLE, mapTypeControl: false }}
          onLoad={setAnalyticsMap}
          onUnmount={() => setAnalyticsMap(null)}
        >
          <AnalyticsHeatmapLayer
            map={analyticsMap}
            points={heatmapPoints}
            maxIntensity={heatmapMaxIntensity}
          />
        </GoogleMap>
      </LoadScript>
    </div>
  );
};

export default PodcastAnalyticsDashboard;
